import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { base44, getToken } from '@/api/base44Client';
import { cacheValue, readCachedValue } from '@/lib/offlineCache';

/**
 * Who is signed in.
 *
 * Rewritten for the new API. The Base44 version could not simply be ported:
 * it imported the Base44 SDK directly, read an `appParams` bundle out of the
 * URL, and began by fetching Base44's own "public settings" over a second
 * axios client before it would even look at the user. None of that exists now.
 *
 * The value handed to consumers is deliberately the same set of keys as
 * before, so every component that calls useAuth() is unchanged. Two of them
 * are now vestigial and say so below.
 */

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  const checkUserAuth = useCallback(async () => {
    // No token means nobody is signed in. Asking the server would be a
    // guaranteed 401 and a wasted round trip on a cold start.
    if (!getToken()) {
      setUser(null);
      setIsAuthenticated(false);
      setIsLoadingAuth(false);
      setAuthChecked(true);
      return;
    }

    try {
      setIsLoadingAuth(true);
      const currentUser = await base44.auth.me();
      setUser(currentUser);
      cacheValue('user', currentUser);
      setIsAuthenticated(true);
      setAuthError(null);
    } catch (error) {
      if (error.status === 0) {
        // Unreachable, not refused: a real 401/403 means the server looked
        // at this token and said no, but status 0 means the server was
        // never asked at all. Treating those the same would bounce a rep
        // with no signal to the login screen on every cold start, which is
        // exactly the failure the offline outbox exists to prevent one
        // layer up (a queued capture with nowhere to be captured from). The
        // stored token is trusted provisionally, alongside the last known
        // user record, until a real answer says otherwise.
        const cachedUser = readCachedValue('user');
        if (cachedUser) {
          setUser(cachedUser);
          setIsAuthenticated(true);
          setAuthError(null);
          return;
        }
      }
      setUser(null);
      setIsAuthenticated(false);
      if (error.status === 401 || error.status === 403) {
        // Expected: the token expired, was revoked, or the account was
        // suspended. Not worth a console error every time.
        setAuthError({ type: 'auth_required', message: 'Please sign in again.' });
      } else {
        console.error('Could not check who is signed in:', error);
        setAuthError({ type: 'unknown', message: error.message });
      }
    } finally {
      setIsLoadingAuth(false);
      setAuthChecked(true);
    }
  }, []);

  useEffect(() => {
    checkUserAuth();
  }, [checkUserAuth]);

  const logout = (shouldRedirect = true) => {
    setUser(null);
    setIsAuthenticated(false);
    // base44.auth.logout ends the session on the server, drops the token and
    // sends the browser to /login.
    base44.auth.logout(shouldRedirect ? window.location.href : undefined);
  };

  const navigateToLogin = () => {
    base44.auth.redirectToLogin();
  };

  /**
   * Base44 kept approval in a separate UserApproval table, which had zero rows
   * in production and is cut. Account state is users.status now, so this is
   * derived rather than fetched. ProtectedRoute reads it.
   */
  const approvalStatus = !user ? null : user.status === 'active' ? 'approved' : user.status;

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      isLoadingAuth,
      authError,
      authChecked,
      approvalStatus,
      isAdmin: user?.role === 'admin',
      logout,
      navigateToLogin,
      checkUserAuth,
      refreshApproval: checkUserAuth,

      // Vestigial, kept so components that read them still render. Base44
      // fetched a per app settings blob before showing anything; TradeSync is
      // one app for one company and has nothing to fetch.
      isLoadingPublicSettings: false,
      appPublicSettings: null,
      checkAppState: checkUserAuth,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
